// src/services/fileProtectionService.ts
import { getApiUrl, isServer, getApiToken } from "../utils/env";

export interface FileAuthResponse {
  requiresAuth: boolean;
}

export interface AuthResponse {
  success: boolean;
  accessToken?: string;
  downloadUrl?: string;
  simpleDownloadUrl?: string;
  message: string;
}

export interface DownloadResponse {
  success: boolean;
  downloadUrl?: string;
  message: string;
}

class FileProtectionService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = getApiUrl();
  }

  private getApiPath(path: string): string {
    // On server: direct API call
    if (isServer()) {
      return `${this.apiUrl}${path}`;
    }
    // On client: use proxy
    return `/api/proxy${path}`;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // Only add token on server
    if (isServer()) {
      headers.Authorization = "Bearer " + getApiToken();
    }

    return headers;
  }

  // Check if file requires authentication
  async checkFileAccess(fileUrl: string): Promise<FileAuthResponse> {
    const response = await fetch(this.getApiPath("/api/file-access/check"), {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({ fileUrl }),
    });

    if (!response.ok) {
      throw new Error("Failed to check file access");
    }

    return response.json();
  }

  // Authenticate user for file access
  async authenticateFileAccess(
    login: string,
    password: string,
    fileUrl: string,
  ): Promise<AuthResponse> {
    const response = await fetch(
      this.getApiPath("/api/file-access/authenticate"),
      {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ login, password, fileUrl }),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Authentication failed");
    }

    return response.json();
  }

  // Download protected file
  async downloadProtectedFile(
    fileUrl: string,
    accessToken: string,
  ): Promise<DownloadResponse> {
    const response = await fetch(this.getApiPath("/api/file-access/download"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ fileUrl }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Download failed");
    }

    return response.json();
  }
}

export const fileProtectionService = new FileProtectionService();
